import { useIonRouter } from '@ionic/react';
import {
  Permission,
  SubscriptionStatus,
  type Workspace,
  WorkspacesApi,
} from '@up2stay/up2promo-api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@up2stay/up2promo-ui';
import { useState } from 'react';
import { toast } from 'sonner';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import { DangerZone } from '@/core/components/settings/danger-zone';
import { SettingsPageShell } from '@/core/components/settings/settings-page-shell';
import { BasicInfoSection } from '@/core/components/workspace/settings/basic-info-section';
import { LeadsVisibilitySection } from '@/core/components/workspace/settings/leads-visibility-section';
import { SecuritySection } from '@/core/components/workspace/settings/security-section';
import { UserManagementSection } from '@/core/components/workspace/settings/user-management-section';
import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';
import { usePatchWorkspaceInMemberships } from '@/core/hooks/use-memberships-query';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import {
  useCurrentWorkspace,
  useSetWorkspace,
} from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';

const SECTION_IDS = {
  overview: 'workspace-settings-overview',
  team: 'workspace-settings-team',
  leads: 'workspace-settings-leads',
  security: 'workspace-settings-security',
  danger: 'workspace-settings-danger',
} as const;

function WorkspaceSettingsBreadcrumb({
  domainLabel,
  settingsLabel,
}: {
  domainLabel: string;
  settingsLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {settingsLabel}
    </>
  );
}

export function WorkspaceSettingsPage() {
  const dict = useDictionary();
  const workspaceDict = dict.workspace.settings;
  const settingsDict = dict.settings;
  const currentWorkspace = useCurrentWorkspace();
  const setWorkspace = useSetWorkspace();
  const patchWorkspaceInMembershipsCache = usePatchWorkspaceInMemberships();
  const { refetch: refetchWorkspaces } = useWorkspaces();
  const router = useIonRouter();
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  /** `workspace:delete` is owner-only and excluded from custom roles. */
  const canDeleteWorkspace = can(Permission.WORKSPACE_DELETE);
  const [isSavingLeadsVisibility, setIsSavingLeadsVisibility] = useState(false);
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);
  const {
    status: leadsAutosaveStatus,
    markSaving: markLeadsSaving,
    markSaved: markLeadsSaved,
    markError: markLeadsError,
  } = useAutosaveCue();

  const breadcrumb = (
    <WorkspaceSettingsBreadcrumb
      domainLabel={dict.nav.navDomainWorkspaceAdmin}
      settingsLabel={dict.nav.settings}
    />
  );

  const hasActivePaidSubscription =
    currentWorkspace?.plan !== 'free' &&
    (currentWorkspace?.subscriptionStatus === SubscriptionStatus.ACTIVE ||
      currentWorkspace?.subscriptionStatus === SubscriptionStatus.TRIALING ||
      currentWorkspace?.subscriptionStatus === SubscriptionStatus.PAST_DUE);

  const deleteDialogDescription = hasActivePaidSubscription
    ? `${workspaceDict.dangerZone.deleteDialogDescription} ${workspaceDict.dangerZone.deleteDialogSubscriptionNote}`
    : workspaceDict.dangerZone.deleteDialogDescription;

  const applyWorkspaceUpdate = (workspace: Workspace) => {
    patchWorkspaceInMembershipsCache(workspace);
    setWorkspace(workspace);
  };

  const handleWorkspaceUpdated = (workspace: Workspace) => {
    applyWorkspaceUpdate(workspace);
  };

  const handleDeleteWorkspace = async () => {
    if (!currentWorkspace) return;
    const deletedId = currentWorkspace.id;

    await WorkspacesApi.remove({ path: { id: deletedId } });

    const remaining = await refetchWorkspaces();
    const [nextWorkspace] = remaining;
    if (nextWorkspace) {
      setWorkspace(nextWorkspace);
      navigateShell(shellPaths.home);
    } else {
      setWorkspace(null);
      router.push('/onboarding', 'root', 'replace');
    }
  };

  const persistLeadsVisibility = async (checked: boolean) => {
    if (!currentWorkspace || isSavingLeadsVisibility) return;
    const previous = currentWorkspace;
    applyWorkspaceUpdate({
      ...currentWorkspace,
      leadsAlwaysSharedWithWorkspace: checked,
    });
    setIsSavingLeadsVisibility(true);
    markLeadsSaving();
    try {
      const response = await WorkspacesApi.update({
        path: { id: currentWorkspace.id },
        body: { leadsAlwaysSharedWithWorkspace: checked },
      });
      if (response.data) {
        applyWorkspaceUpdate(response.data);
        markLeadsSaved();
        toast.success(workspaceDict.leadsVisibility.saveSuccess);
        return;
      }
      applyWorkspaceUpdate(previous);
      markLeadsError();
      toast.error(workspaceDict.leadsVisibility.saveError);
    } catch (error) {
      console.error('Failed to update leads visibility:', error);
      applyWorkspaceUpdate(previous);
      markLeadsError();
      toast.error(workspaceDict.leadsVisibility.saveError);
    } finally {
      setIsSavingLeadsVisibility(false);
    }
  };

  const handleLeadsVisibilityChange = (checked: boolean) => {
    if (!currentWorkspace || isSavingLeadsVisibility) return;
    if (checked && !currentWorkspace.leadsAlwaysSharedWithWorkspace) {
      setConfirmEnableOpen(true);
      return;
    }
    void persistLeadsVisibility(checked);
  };

  if (!currentWorkspace) {
    return (
      <AppIonPage>
        <AppPageContent>
          <AppPageLayout>
            <PageHeader title={workspaceDict.title} breadcrumb={breadcrumb} />
            <p className="text-[13.5px] leading-relaxed text-muted-foreground">
              {workspaceDict.selectWorkspace}
            </p>
          </AppPageLayout>
        </AppPageContent>
      </AppIonPage>
    );
  }

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader breadcrumb={breadcrumb} title={workspaceDict.title} />
          <SettingsPageShell>
            <BasicInfoSection
              sectionId={SECTION_IDS.overview}
              workspace={currentWorkspace}
              onWorkspaceUpdated={handleWorkspaceUpdated}
              workspaceDict={workspaceDict}
              saveLabel={settingsDict.save}
              savingLabel={settingsDict.saving}
            />

            <UserManagementSection
              sectionId={SECTION_IDS.team}
              dict={workspaceDict.userManagement}
            />

            <LeadsVisibilitySection
              sectionId={SECTION_IDS.leads}
              dict={workspaceDict.leadsVisibility}
              checked={currentWorkspace.leadsAlwaysSharedWithWorkspace ?? false}
              disabled={!currentWorkspace}
              pending={isSavingLeadsVisibility}
              autosaveStatus={leadsAutosaveStatus}
              onCheckedChange={handleLeadsVisibilityChange}
            />

            <SecuritySection
              sectionId={SECTION_IDS.security}
              dict={workspaceDict.security}
            />

            {canDeleteWorkspace ? (
              <DangerZone
                sectionId={SECTION_IDS.danger}
                title={workspaceDict.dangerZone.title}
                description={workspaceDict.dangerZone.description}
                deleteTitle={workspaceDict.dangerZone.deleteTitle}
                deleteDescription={workspaceDict.dangerZone.deleteDescription}
                deleteButtonLabel={workspaceDict.dangerZone.deleteButton}
                expandLabel={
                  workspaceDict.dangerZone.expand ?? 'Show destructive actions'
                }
                collapseLabel={
                  workspaceDict.dangerZone.collapse ??
                  'Hide destructive actions'
                }
                onDelete={handleDeleteWorkspace}
                confirmName={currentWorkspace.name || ''}
                dict={{
                  dialogTitle: workspaceDict.dangerZone.deleteDialogTitle,
                  dialogDescription: deleteDialogDescription,
                  confirmLabel:
                    workspaceDict.dangerZone.deleteDialogConfirmLabel,
                  confirmPlaceholder:
                    workspaceDict.dangerZone.deleteDialogConfirmPlaceholder,
                  cancel: workspaceDict.dangerZone.deleteDialogCancel,
                  confirm: workspaceDict.dangerZone.deleteDialogConfirm,
                  nameMismatch:
                    workspaceDict.dangerZone.deleteDialogNameMismatch,
                  success: workspaceDict.dangerZone.deleteSuccess,
                  error: workspaceDict.dangerZone.deleteError,
                }}
              />
            ) : null}
          </SettingsPageShell>
        </AppPageLayout>
      </AppPageContent>
      <AlertDialog open={confirmEnableOpen} onOpenChange={setConfirmEnableOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {workspaceDict.leadsVisibility.enableConfirmTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {workspaceDict.leadsVisibility.enableConfirmDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {workspaceDict.leadsVisibility.enableConfirmCancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmEnableOpen(false);
                void persistLeadsVisibility(true);
              }}
            >
              {workspaceDict.leadsVisibility.enableConfirmAction}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppIonPage>
  );
}
